<template>
  <div class="product-listing-price-wrapper">
    <div class="prices">
      <span  
        v-if="hasVariant" 
        class="from-text"
        :class="{'price-new': discountPrice && displayRedPrice }"> 
        {{ $translate('C_PRICE_FROM') }} 
      </span>

      <span 
        v-if="discountPrice"
        class="price"
        :class="{ 'price-new': displayRedPrice }"
      >
        {{ formattedDiscountPrice }}
      </span>

      <span 
        v-if="regularPrice && !discountPrice" 
        class="price"
      >
        {{ formattedRegularPrice }}
      </span>

      <span 
        :class="{ 'vat-text': true, 'price-new': discountPrice && displayRedPrice  }"
      > 
        {{ showVat ?  $translate("INCL_VAT") : $translate("EXCL_VAT") }}
      </span>

      <span 
        v-if="regularPrice && discountPrice && displayRedPrice"
        :class="{ 'price-old': discountPrice }"
      >
        {{ formattedRegularPrice }}
      </span>

      <span 
        v-if="regularPrice && discountPrice && displayRedPrice"
        class="ord-text"
      >
        ({{ $translate("C_ORIGINAL_REC_TEXT")  }})
      </span>

      <tooltip
        v-if="regularPrice && discountPrice && displayRedPrice"
        :text="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')"
        class="info-icon"
      >
        <InfoIcon :aria-label="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')" />
      </tooltip>  
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, watch, computed } from 'vue';
import InfoIcon from '@/assets/icons/information-blue-rounded.svg?component';
import { useProductPrice } from '@/composables/product-price.js';
import { useCurrencyFormatter } from '@/composables/use-currency-formatter.js';

export default {
  props: {
    showVat: {
      type: Boolean,
      required: true
    },
    adminLoggedIn: {
      type: Boolean,
      required: false,
      default: false
    },
    updateOnChange: {
      type: Boolean,
      required: false
    },
    productData: {
      type: Object,
      required: true
    },
    loggedIn: {
      type: Boolean,
      required: true
    },
    handlePriceLogUpdate: {
      type: Boolean,
      required: false,
      default: false
    },
    currency: {
      type: String,
      required: false,
      default: 'SEK'
    }
  },
  components: {
    InfoIcon
  },
  setup(props) {
    const productData = ref(props.productData);
    const hasVariant = ref(productData.value.has_variant);
    const cheapestVariantPriceRaw = ref(productData.value.cheapest_variant_price_raw);
    const cheapestVariantPrice = ref(productData.value.cheapest_variant_price);

    productData.value.id = parseInt(productData.value.id);
    productData.value.lowest_number = parseInt(productData.value.lowest_number);
    productData.value.price_discount_percentage = parseInt(productData.value.price_discount_percentage?.replace('%', ''));

    const {
      intialPriceLoaded,
      price,
      handleUpdatedProductConfiguratorPrice,
      getPrice,
      discountPrice,
      regularPrice,
      discountPercentage,
      vatText,
      displayRedPrice,
      priceRaw,
      fetchErpPrice,
      fetchErpPricesForVariants,
      b2cFromPrice
    } = useProductPrice(props.productData, props);

    const { formatPrice } = useCurrencyFormatter(props.currency);
    
    const formattedDiscountPrice = computed(() => {
      return discountPrice.value != null ? formatPrice(discountPrice.value) : null;
    });

    const formattedRegularPrice = computed(() => {
      return regularPrice.value != null ? formatPrice(regularPrice.value) : null;
    });

    onMounted(async () => {
      if (props.updateOnChange && props.productData.id) {
        await getPrice(props.productData.id);
        const productForm = document.querySelector('form[name=add_product]');
        if (productForm) {
          productForm.addEventListener('input', getPrice);
        }
      }

      document.addEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );

      intialPriceLoaded.value = true;

      if (props.adminLoggedIn) {
        validatePriceConsistency();
      }

      if (props.loggedIn && hasVariant.value && Array.isArray(productData.value.variant_ids)) {
        await fetchErpPricesForVariants(productData.value.variant_ids);
      } else if (props.loggedIn) {
        await fetchErpPrice(productData.value.id);
      } else if (hasVariant.value) {
        b2cFromPrice();
      }
    });

    onUnmounted(() => {
      document.removeEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );
      const productForm = document.querySelector('form[name=add_product]');
      if (productForm) {
        productForm.removeEventListener('input', getPrice);
      }
    });

    const validatePriceConsistency = () => {
      const validate = () => {
        const structuredDataScript = document.getElementById('item-list-ld-json');
        if (structuredDataScript) {
          const structuredDataJson = JSON.parse(structuredDataScript.textContent);
          const finalPrice = discountPrice.value || regularPrice.value;

          structuredDataJson.itemListElement.forEach(item => {
            const structuredPrice = item.price;
            if (props.productData.id == item.internalId && structuredPrice != finalPrice) {
              console.warn(`Price discrepancy observed:\nThe displayed price for the product with id: ${props.productData.id},\ndoes not match the price indicated in the structured data.\nDisplayed price: ${finalPrice}\nStructured data price: ${structuredPrice}`);
              stopWatch();
            };
          });
        };
      };

      const stopWatch = watch([discountPrice, regularPrice], () => {
        validate();
      });
    };

    return {
      InfoIcon,
      vatText,
      displayRedPrice,
      hasVariant,
      discountPrice,
      regularPrice,
      formattedDiscountPrice,
      formattedRegularPrice,
      cheapestVariantPriceRaw,
      cheapestVariantPrice
    };
  }
};
</script>
